import { z } from 'zod';
import { AnnouncementAudienceSchema, AnnouncementChannelsSchema, AnnouncementKindSchema, AnnouncementStatusSchema, } from '../announcement.js';
import { isNotificationHref } from '../notification.js';
import { HumanAccountRoleSchema } from './account.js';
import { createPaginatedResponseSchema, LowercaseUuidSchema } from './common.js';
// =============================================================================
// Operator-sent announcements (super-admin)
// =============================================================================
export const ANNOUNCEMENT_TITLE_MAX_LENGTH = 120;
export const ANNOUNCEMENT_BODY_MAX_LENGTH = 1000;
export const ANNOUNCEMENT_LINK_MAX_LENGTH = 2048;
/**
 * What an operator writes for an announcement. The body is plain text: blank
 * lines separate paragraphs, and the email template escapes it. `link` is an app
 * path or an `https:` URL. `accountIds` is the chosen accounts for an `ACCOUNTS`
 * audience and must be empty for `ALL`. `excludedAccountIds` is the accounts an
 * `ALL` audience skips and must be empty for `ACCOUNTS`. `roles` narrows the whole audience to
 * those roles; empty means every human role. `channels` is where it is delivered:
 * the bell only, or the bell and email.
 */
const announcementInputObject = z.strictObject({
    kind: AnnouncementKindSchema,
    title: z
        .string()
        .trim()
        .min(1, 'A title is required')
        .max(ANNOUNCEMENT_TITLE_MAX_LENGTH)
        .regex(/^[^\r\n]*$/, 'A title must be one line'),
    body: z.string().trim().min(1, 'A message is required').max(ANNOUNCEMENT_BODY_MAX_LENGTH),
    link: z
        .string()
        .max(ANNOUNCEMENT_LINK_MAX_LENGTH)
        .refine(isNotificationHref, 'A link must be an https address or a path within the app')
        .nullable(),
    audience: AnnouncementAudienceSchema,
    channels: AnnouncementChannelsSchema,
    accountIds: z.array(LowercaseUuidSchema),
    excludedAccountIds: z.array(LowercaseUuidSchema),
    roles: z.array(HumanAccountRoleSchema),
});
function refineAnnouncementInput(input, ctx) {
    if (input.audience === 'ACCOUNTS' && input.accountIds.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['accountIds'], message: 'Choose at least one account' });
    }
    if (input.audience === 'ALL' && input.accountIds.length > 0) {
        ctx.addIssue({ code: 'custom', path: ['accountIds'], message: 'Accounts can be chosen only when the audience is chosen accounts' });
    }
    if (new Set(input.accountIds).size !== input.accountIds.length) {
        ctx.addIssue({ code: 'custom', path: ['accountIds'], message: 'An account can be chosen only once' });
    }
    if (input.audience === 'ACCOUNTS' && input.excludedAccountIds.length > 0) {
        ctx.addIssue({
            code: 'custom',
            path: ['excludedAccountIds'],
            message: 'Accounts can be left out only when the audience is every account; leave them off the chosen accounts instead',
        });
    }
    if (new Set(input.excludedAccountIds).size !== input.excludedAccountIds.length) {
        ctx.addIssue({ code: 'custom', path: ['excludedAccountIds'], message: 'An account can be left out only once' });
    }
    if (new Set(input.roles).size !== input.roles.length) {
        ctx.addIssue({ code: 'custom', path: ['roles'], message: 'A role can be chosen only once' });
    }
}
export const AnnouncementInputSchema = announcementInputObject.superRefine(refineAnnouncementInput);
export const AdminAnnouncementCreateBodySchema = announcementInputObject
    .extend({ idempotencyKey: z.string().min(1) })
    .superRefine(refineAnnouncementInput);
export const AdminAnnouncementParamsSchema = z.strictObject({ id: LowercaseUuidSchema });
export const AdminAnnouncementListQuerySchema = z.strictObject({
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).prefault(20),
});
/**
 * Schedule an announcement now (`sendNow`, picked up by the next dispatcher tick,
 * within 15 minutes) or at a future time. Exactly one of the two.
 */
export const AdminAnnouncementScheduleBodySchema = z.union([
    z.strictObject({ sendNow: z.boolean() }).refine((body) => body.sendNow, { error: 'sendNow must be true' }),
    z.strictObject({ scheduledAt: z.iso.datetime({ error: 'scheduledAt must be a valid ISO datetime string' }) }),
]);
/**
 * An announcement as the operator sees it. `recipientCount` is null until the
 * send starts; `raisedCount` and `skippedCount` are the outcome counts as they
 * stand and are not guaranteed to sum to `recipientCount`.
 */
export const AdminAnnouncementSchema = z.object({
    id: z.string(),
    kind: AnnouncementKindSchema,
    title: z.string(),
    body: z.string(),
    link: z.string().nullable(),
    audience: AnnouncementAudienceSchema,
    channels: AnnouncementChannelsSchema,
    accountIds: z.array(z.string()),
    excludedAccountIds: z.array(z.string()),
    roles: z.array(HumanAccountRoleSchema),
    status: AnnouncementStatusSchema,
    scheduledAt: z.string().nullable(),
    createdByUserId: z.string().nullable(),
    dispatchStartedAt: z.string().nullable(),
    dispatchedAt: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    recipientCount: z.number().int().nullable(),
    raisedCount: z.number().int(),
    skippedCount: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const AdminAnnouncementListResponseSchema = createPaginatedResponseSchema(AdminAnnouncementSchema);
export const AdminAnnouncementReachResponseSchema = z.object({
    userCount: z.number().int(),
    accountCount: z.number().int(),
    /** Zero for an in-app-only announcement. Otherwise ignores delivery hours and addresses the email provider has suppressed. */
    approximateEmailCount: z.number().int(),
    /** Chosen accounts that can receive nothing (outside the notifications programme, or administered entities). */
    unreachableAccountIds: z.array(z.string()),
});
export const AdminAnnouncementPreviewResponseSchema = z.object({
    bell: z.object({ title: z.string(), body: z.string(), href: z.string().nullable() }),
    /** Null when the audience resolves to nobody or the announcement is in-app only. The email names the sample account's practice. */
    email: z
        .object({ subject: z.string(), html: z.string(), text: z.string(), sampleAccountId: z.string() })
        .nullable(),
});
export const E2EAnnouncementDispatchBodySchema = z.strictObject({ announcementId: LowercaseUuidSchema });
export const E2EAnnouncementDispatchResponseSchema = z.object({ recipientCount: z.number().int().nullable() });
